/**
 * 3D Avatar Creator for Veridian HVN Metaverse
 * Three.js-powered procedural 3D character generation
 *
 * Transforms text descriptions into unique 3D avatars with
 * wings, armor, halos, particle auras, and interactive rotation.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// ─── Color Palettes ──────────────────────────────────────────────
const PALETTES = {
  warm:      { primary: 0xFFD700, secondary: 0xFF8C00, accent: 0xFF6347, skin: 0xE8B87A, glow: 0xFFD700, emissive: 0x332200 },
  cool:      { primary: 0x4169E1, secondary: 0x1E90FF, accent: 0x00CED1, skin: 0xB0C4DE, glow: 0x4169E1, emissive: 0x001133 },
  mystical:  { primary: 0x9370DB, secondary: 0x8A2BE2, accent: 0xBA55D3, skin: 0xD8BFD8, glow: 0x9370DB, emissive: 0x220033 },
  shadow:    { primary: 0x2F4F4F, secondary: 0x708090, accent: 0xFF1493, skin: 0x696969, glow: 0xFF1493, emissive: 0x110011 },
  nature:    { primary: 0x228B22, secondary: 0x32CD32, accent: 0xFFD700, skin: 0x8FBC8F, glow: 0x32CD32, emissive: 0x002200 },
  tech:      { primary: 0x00FF00, secondary: 0x0099FF, accent: 0xFF00FF, skin: 0x333344, glow: 0x00FFFF, emissive: 0x001122 },
  celestial: { primary: 0x00BFFF, secondary: 0x4169E1, accent: 0xFFB6C1, skin: 0xF0F8FF, glow: 0x87CEEB, emissive: 0x112244 },
  infernal:  { primary: 0xFF4500, secondary: 0x8B0000, accent: 0xFF6600, skin: 0x8B0000, glow: 0xFF4500, emissive: 0x330000 },
};

// ─── Description Analyzer ────────────────────────────────────────
function analyzeDescription(description) {
  const t = description.toLowerCase();

  // Detect palette mood
  let mood = 'mystical';
  if (t.match(/warm|golden|sunny|fire|lava|sun|flame/)) mood = 'warm';
  else if (t.match(/cool|ice|frost|blue|water|ocean|frozen/)) mood = 'cool';
  else if (t.match(/shadow|dark|night|noir|stealth|black/)) mood = 'shadow';
  else if (t.match(/nature|forest|earth|green|plant|druid|leaf/)) mood = 'nature';
  else if (t.match(/tech|cyber|digital|neon|electric|code|robot|mech/)) mood = 'tech';
  else if (t.match(/celestial|cosmic|star|space|galaxy|nebula|heaven/)) mood = 'celestial';
  else if (t.match(/infernal|demon|hell|lava|magma|devil|fire/)) mood = 'infernal';
  else if (t.match(/angel|divine|holy|light|radiant/)) mood = 'warm';

  // Detect features
  const features = {
    hasWings:    !!t.match(/wing|fly|feather|angel|bird|bat|dragon/),
    wingStyle:   t.match(/bat|demon|dragon/) ? 'bat' : t.match(/energy|cosmic|tech/) ? 'energy' : 'feathered',
    hasHalo:     !!t.match(/halo|divine|holy|angel|saint|radiant/),
    hasArmor:    !!t.match(/armor|armour|metal|steel|chain|plate|knight|warrior|guardian/),
    armorStyle:  t.match(/heavy|plate|tank/) ? 'heavy' : t.match(/tech|cyber|mech/) ? 'tech' : 'light',
    hasAura:     !t.match(/stealth|hidden|invisible/),
    hasCrown:    !!t.match(/crown|king|queen|royal|emperor/),
    hasHorns:    !!t.match(/horn|demon|devil|ram|bull/),
    hasTail:     !!t.match(/tail|dragon|demon|devil|fox/),
    hasWeapon:   !!t.match(/sword|blade|staff|spear|bow|axe|weapon/),
    weaponType:  t.match(/staff|wand|magic/) ? 'staff' : t.match(/bow/) ? 'bow' : 'sword',
    bodyType:    t.match(/large|big|heavy|tank|giant/) ? 'heavy' : t.match(/slim|thin|lithe|agile/) ? 'slim' : 'medium',
    isRobot:     !!t.match(/robot|mech|android|cyborg|automaton/),
    isUndead:    !!t.match(/undead|skeleton|ghost|spirit|wraith|lich/),
  };

  return { mood, features };
}

// ─── Seeded Random ───────────────────────────────────────────────
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function makeRng(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

// ─── 3D Character Builder ────────────────────────────────────────

function buildCharacter(scene, description, palette, features, rng) {
  const group = new THREE.Group();
  group.name = 'avatar';

  const primaryColor = new THREE.Color(palette.primary);
  const secondaryColor = new THREE.Color(palette.secondary);
  const accentColor = new THREE.Color(palette.accent);
  const skinColor = new THREE.Color(palette.skin);
  const glowColor = new THREE.Color(palette.glow);

  // Material factory
  const makeMat = (color, opts = {}) => new THREE.MeshStandardMaterial({
    color,
    roughness: opts.roughness ?? 0.4,
    metalness: opts.metalness ?? 0.3,
    emissive: opts.emissive ?? new THREE.Color(palette.emissive),
    emissiveIntensity: opts.emissiveIntensity ?? 0.15,
    ...opts,
  });

  const skinMat = makeMat(skinColor, { roughness: 0.6, metalness: 0.1 });
  const primaryMat = makeMat(primaryColor, { metalness: 0.6, roughness: 0.2 });
  const accentMat = makeMat(accentColor, { metalness: 0.5 });

  // Scale factors based on body type
  const bodyScale = features.bodyType === 'heavy' ? 1.2 : features.bodyType === 'slim' ? 0.8 : 1.0;
  const shoulderW = 0.55 * bodyScale;

  // ── HEAD ──
  const headGroup = new THREE.Group();
  const headGeo = new THREE.SphereGeometry(0.32, 32, 32);
  const head = new THREE.Mesh(headGeo, features.isRobot ? primaryMat : skinMat);
  headGroup.add(head);

  // Eyes
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0x111111,
    emissive: glowColor,
    emissiveIntensity: 0.8,
    roughness: 0.1,
    metalness: 0.8,
  });
  const eyeGeo = new THREE.SphereGeometry(0.055, 16, 16);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.11, 0.05, 0.27);
  leftEye.scale.set(1, 1.2, 0.6);
  headGroup.add(leftEye);

  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.11, 0.05, 0.27);
  rightEye.scale.set(1, 1.2, 0.6);
  headGroup.add(rightEye);

  // Eye glow points
  const eyeGlowMat = new THREE.MeshBasicMaterial({ color: glowColor });
  const eyeGlowGeo = new THREE.SphereGeometry(0.025, 8, 8);
  const leftGlow = new THREE.Mesh(eyeGlowGeo, eyeGlowMat);
  leftGlow.position.set(-0.11, 0.06, 0.30);
  headGroup.add(leftGlow);
  const rightGlow = new THREE.Mesh(eyeGlowGeo, eyeGlowMat);
  rightGlow.position.set(0.11, 0.06, 0.30);
  headGroup.add(rightGlow);

  // Mouth - subtle line
  const mouthGeo = new THREE.BoxGeometry(0.12, 0.015, 0.02);
  const mouthMat = makeMat(0x333333, { roughness: 0.8 });
  const mouth = new THREE.Mesh(mouthGeo, mouthMat);
  mouth.position.set(0, -0.1, 0.28);
  headGroup.add(mouth);

  // Horns
  if (features.hasHorns) {
    const hornMat = makeMat(0x444444, { metalness: 0.7, roughness: 0.3 });
    const hornGeo = new THREE.ConeGeometry(0.05, 0.35, 8);
    const leftHorn = new THREE.Mesh(hornGeo, hornMat);
    leftHorn.position.set(-0.18, 0.28, 0);
    leftHorn.rotation.z = 0.3;
    headGroup.add(leftHorn);
    const rightHorn = new THREE.Mesh(hornGeo, hornMat);
    rightHorn.position.set(0.18, 0.28, 0);
    rightHorn.rotation.z = -0.3;
    headGroup.add(rightHorn);
  }

  // Crown
  if (features.hasCrown) {
    const crownMat = makeMat(0xFFD700, { metalness: 0.9, roughness: 0.1, emissiveIntensity: 0.4, emissive: new THREE.Color(0x443300) });
    const crownGeo = new THREE.CylinderGeometry(0.25, 0.30, 0.12, 6);
    const crown = new THREE.Mesh(crownGeo, crownMat);
    crown.position.set(0, 0.32, 0);
    headGroup.add(crown);
    // Crown points
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const pointGeo = new THREE.ConeGeometry(0.03, 0.1, 4);
      const point = new THREE.Mesh(pointGeo, crownMat);
      point.position.set(Math.cos(angle) * 0.24, 0.42, Math.sin(angle) * 0.24);
      headGroup.add(point);
    }
  }

  headGroup.position.y = 1.55;
  group.add(headGroup);

  // ── NECK ──
  const neckGeo = new THREE.CylinderGeometry(0.1, 0.12, 0.15, 12);
  const neck = new THREE.Mesh(neckGeo, skinMat);
  neck.position.y = 1.32;
  group.add(neck);

  // ── TORSO ──
  const torsoGroup = new THREE.Group();

  // Upper body - tapered cylinder
  const torsoGeo = new THREE.CylinderGeometry(0.22 * bodyScale, 0.3 * bodyScale, 0.6, 12);
  const torsoMat = features.hasArmor ? primaryMat : makeMat(palette.secondary, { roughness: 0.5 });
  const torso = new THREE.Mesh(torsoGeo, torsoMat);
  torso.position.y = 0.95;
  torsoGroup.add(torso);

  // Lower body
  const lowerGeo = new THREE.CylinderGeometry(0.3 * bodyScale, 0.2 * bodyScale, 0.35, 12);
  const lower = new THREE.Mesh(lowerGeo, torsoMat);
  lower.position.y = 0.52;
  torsoGroup.add(lower);

  // Armor plates
  if (features.hasArmor) {
    const armorMat = makeMat(palette.primary, { metalness: 0.85, roughness: 0.15, emissiveIntensity: 0.3 });

    // Shoulder pads
    const shoulderGeo = new THREE.SphereGeometry(0.16, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    const leftShoulder = new THREE.Mesh(shoulderGeo, armorMat);
    leftShoulder.position.set(-shoulderW, 1.2, 0);
    leftShoulder.scale.set(1.2, 1, 1.2);
    torsoGroup.add(leftShoulder);
    const rightShoulder = new THREE.Mesh(shoulderGeo, armorMat);
    rightShoulder.position.set(shoulderW, 1.2, 0);
    rightShoulder.scale.set(1.2, 1, 1.2);
    torsoGroup.add(rightShoulder);

    // Chest plate
    if (features.armorStyle === 'heavy' || features.armorStyle === 'tech') {
      const chestGeo = new THREE.BoxGeometry(0.45 * bodyScale, 0.35, 0.12);
      const chest = new THREE.Mesh(chestGeo, armorMat);
      chest.position.set(0, 0.95, 0.18);
      torsoGroup.add(chest);

      // Accent gem
      const gemGeo = new THREE.OctahedronGeometry(0.05, 0);
      const gemMat = new THREE.MeshStandardMaterial({
        color: accentColor,
        emissive: accentColor,
        emissiveIntensity: 1.0,
        roughness: 0.0,
        metalness: 0.9,
      });
      const gem = new THREE.Mesh(gemGeo, gemMat);
      gem.position.set(0, 1.0, 0.26);
      gem.name = 'gem';
      torsoGroup.add(gem);
    }

    // Belt
    const beltGeo = new THREE.TorusGeometry(0.25 * bodyScale, 0.03, 8, 24);
    const belt = new THREE.Mesh(beltGeo, armorMat);
    belt.position.y = 0.68;
    belt.rotation.x = Math.PI / 2;
    torsoGroup.add(belt);
  }

  group.add(torsoGroup);

  // ── ARMS ──
  const armMat = features.hasArmor ? primaryMat : skinMat;
  const armGeo = new THREE.CylinderGeometry(0.065, 0.075, 0.55, 8);

  // Left arm
  const leftArm = new THREE.Mesh(armGeo, armMat);
  leftArm.position.set(-shoulderW - 0.02, 0.92, 0);
  leftArm.rotation.z = 0.15;
  group.add(leftArm);

  // Right arm
  const rightArm = new THREE.Mesh(armGeo, armMat);
  rightArm.position.set(shoulderW + 0.02, 0.92, 0);
  rightArm.rotation.z = -0.15;
  group.add(rightArm);

  // Hands
  const handGeo = new THREE.SphereGeometry(0.06, 8, 8);
  const leftHand = new THREE.Mesh(handGeo, skinMat);
  leftHand.position.set(-shoulderW - 0.06, 0.62, 0);
  group.add(leftHand);
  const rightHand = new THREE.Mesh(handGeo, skinMat);
  rightHand.position.set(shoulderW + 0.06, 0.62, 0);
  group.add(rightHand);

  // ── WEAPON ──
  if (features.hasWeapon) {
    const weaponGroup = new THREE.Group();
    if (features.weaponType === 'sword') {
      const bladeMat = makeMat(0xCCCCDD, { metalness: 0.95, roughness: 0.05 });
      const bladeGeo = new THREE.BoxGeometry(0.04, 0.8, 0.015);
      const blade = new THREE.Mesh(bladeGeo, bladeMat);
      blade.position.y = 0.4;
      weaponGroup.add(blade);
      const hiltGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.12, 8);
      const hilt = new THREE.Mesh(hiltGeo, accentMat);
      hilt.rotation.z = Math.PI / 2;
      weaponGroup.add(hilt);
      const guardGeo = new THREE.BoxGeometry(0.15, 0.02, 0.04);
      const guard = new THREE.Mesh(guardGeo, accentMat);
      weaponGroup.add(guard);
    } else if (features.weaponType === 'staff') {
      const staffMat = makeMat(0x8B4513, { metalness: 0.2, roughness: 0.7 });
      const staffGeo = new THREE.CylinderGeometry(0.02, 0.025, 1.5, 8);
      const staff = new THREE.Mesh(staffGeo, staffMat);
      staff.position.y = 0.35;
      weaponGroup.add(staff);
      // Orb on top
      const orbGeo = new THREE.SphereGeometry(0.08, 16, 16);
      const orbMat = new THREE.MeshStandardMaterial({
        color: glowColor,
        emissive: glowColor,
        emissiveIntensity: 1.5,
        roughness: 0.0,
        metalness: 0.2,
        transparent: true,
        opacity: 0.85,
      });
      const orb = new THREE.Mesh(orbGeo, orbMat);
      orb.position.y = 1.15;
      orb.name = 'staffOrb';
      weaponGroup.add(orb);
    }
    weaponGroup.position.set(shoulderW + 0.15, 0.62, 0.1);
    weaponGroup.rotation.z = -0.1;
    group.add(weaponGroup);
  }

  // ── LEGS ──
  const legGeo = new THREE.CylinderGeometry(0.075, 0.065, 0.55, 8);
  const legMat = features.hasArmor ? makeMat(palette.secondary, { metalness: 0.5 }) : makeMat(0x333344);

  const leftLeg = new THREE.Mesh(legGeo, legMat);
  leftLeg.position.set(-0.12, 0.1, 0);
  group.add(leftLeg);

  const rightLeg = new THREE.Mesh(legGeo, legMat);
  rightLeg.position.set(0.12, 0.1, 0);
  group.add(rightLeg);

  // Boots
  const bootGeo = new THREE.BoxGeometry(0.1, 0.1, 0.18);
  const bootMat = features.hasArmor ? primaryMat : makeMat(0x222233);
  const leftBoot = new THREE.Mesh(bootGeo, bootMat);
  leftBoot.position.set(-0.12, -0.2, 0.02);
  group.add(leftBoot);
  const rightBoot = new THREE.Mesh(bootGeo, bootMat);
  rightBoot.position.set(0.12, -0.2, 0.02);
  group.add(rightBoot);

  // ── TAIL ──
  if (features.hasTail) {
    const tailCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0.45, -0.2),
      new THREE.Vector3(0, 0.35, -0.45),
      new THREE.Vector3(0.1, 0.25, -0.65),
      new THREE.Vector3(0.15, 0.35, -0.75),
    ]);
    const tailGeo = new THREE.TubeGeometry(tailCurve, 16, 0.03, 8, false);
    const tail = new THREE.Mesh(tailGeo, primaryMat);
    group.add(tail);
  }

  // ── WINGS ──
  if (features.hasWings) {
    buildWings(group, features.wingStyle, palette, rng, bodyScale);
  }

  // ── HALO ──
  if (features.hasHalo) {
    const haloGeo = new THREE.TorusGeometry(0.35, 0.025, 8, 32);
    const haloMat = new THREE.MeshStandardMaterial({
      color: 0xFFD700,
      emissive: 0xFFD700,
      emissiveIntensity: 1.2,
      roughness: 0.0,
      metalness: 0.8,
      transparent: true,
      opacity: 0.85,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.position.set(0, 1.95, 0);
    halo.rotation.x = Math.PI / 2;
    halo.name = 'halo';
    group.add(halo);

    // Halo glow
    const haloGlowGeo = new THREE.TorusGeometry(0.35, 0.12, 8, 32);
    const haloGlowMat = new THREE.MeshBasicMaterial({
      color: 0xFFD700,
      transparent: true,
      opacity: 0.15,
    });
    const haloGlow = new THREE.Mesh(haloGlowGeo, haloGlowMat);
    haloGlow.position.copy(halo.position);
    haloGlow.rotation.copy(halo.rotation);
    group.add(haloGlow);
  }

  // ── AURA PARTICLES ──
  if (features.hasAura) {
    buildAuraParticles(group, palette, rng);
  }

  // Center the group vertically
  group.position.y = -0.7;

  scene.add(group);
  return group;
}

// ─── Wing Builder ────────────────────────────────────────────────
function buildWings(group, style, palette, rng, bodyScale) {
  const wingMat = new THREE.MeshStandardMaterial({
    color: palette.primary,
    emissive: new THREE.Color(palette.glow),
    emissiveIntensity: 0.3,
    metalness: 0.4,
    roughness: 0.3,
    side: THREE.DoubleSide,
    transparent: style === 'energy',
    opacity: style === 'energy' ? 0.6 : 1.0,
  });

  const wingSpan = 1.2 * bodyScale;

  for (let side = -1; side <= 1; side += 2) {
    const wingGroup = new THREE.Group();
    wingGroup.name = side === -1 ? 'leftWing' : 'rightWing';

    if (style === 'feathered') {
      // Feathered wings - layered planes
      const featherCount = 8;
      for (let i = 0; i < featherCount; i++) {
        const t = i / (featherCount - 1);
        const featherLen = 0.25 + (1 - Math.abs(t - 0.4) * 1.5) * 0.4;
        const featherW = 0.08 + (1 - t) * 0.06;
        const featherGeo = new THREE.PlaneGeometry(featherW, featherLen);
        const feather = new THREE.Mesh(featherGeo, wingMat);

        const angle = -0.3 + t * 1.4;
        const dist = 0.1 + t * wingSpan * 0.8;
        feather.position.set(side * dist, 0.3 - t * 0.5, -0.15);
        feather.rotation.z = side * (-0.5 + t * 0.8);
        feather.rotation.y = side * 0.3;
        wingGroup.add(feather);
      }

      // Wing membrane
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.quadraticCurveTo(side * wingSpan * 0.5, 0.6, side * wingSpan, 0.2);
      shape.quadraticCurveTo(side * wingSpan * 0.7, -0.3, side * wingSpan * 0.3, -0.4);
      shape.lineTo(0, -0.1);
      shape.closePath();
      const shapeGeo = new THREE.ShapeGeometry(shape);
      const membraneMat = new THREE.MeshStandardMaterial({
        color: palette.primary,
        emissive: new THREE.Color(palette.emissive),
        emissiveIntensity: 0.3,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5,
        metalness: 0.2,
        roughness: 0.5,
      });
      const membrane = new THREE.Mesh(shapeGeo, membraneMat);
      membrane.position.set(0, 0.2, -0.18);
      wingGroup.add(membrane);

    } else if (style === 'bat') {
      // Bat/dragon wings - angular geometry
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.lineTo(side * wingSpan * 0.3, 0.6);
      shape.lineTo(side * wingSpan * 0.7, 0.5);
      shape.lineTo(side * wingSpan, 0.1);
      shape.lineTo(side * wingSpan * 0.8, -0.2);
      shape.lineTo(side * wingSpan * 0.4, -0.35);
      shape.lineTo(0, -0.15);
      shape.closePath();
      const shapeGeo = new THREE.ShapeGeometry(shape);
      const batMat = new THREE.MeshStandardMaterial({
        color: palette.secondary,
        emissive: new THREE.Color(palette.emissive),
        emissiveIntensity: 0.2,
        side: THREE.DoubleSide,
        metalness: 0.3,
        roughness: 0.6,
      });
      const membrane = new THREE.Mesh(shapeGeo, batMat);
      membrane.position.set(0, 0.2, -0.18);
      wingGroup.add(membrane);

      // Wing bones
      const boneMat = new THREE.MeshStandardMaterial({ color: 0x222222, metalness: 0.7, roughness: 0.3 });
      for (let b = 0; b < 3; b++) {
        const boneGeo = new THREE.CylinderGeometry(0.01, 0.008, wingSpan * (0.5 + b * 0.2), 4);
        const bone = new THREE.Mesh(boneGeo, boneMat);
        bone.position.set(side * wingSpan * (0.2 + b * 0.25), 0.35 - b * 0.15, -0.17);
        bone.rotation.z = side * (0.3 + b * 0.25);
        wingGroup.add(bone);
      }

    } else {
      // Energy wings - glowing translucent
      const energyMat = new THREE.MeshBasicMaterial({
        color: palette.glow,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide,
      });
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.quadraticCurveTo(side * wingSpan * 0.4, 0.7, side * wingSpan * 0.9, 0.3);
      shape.quadraticCurveTo(side * wingSpan * 0.6, -0.1, side * wingSpan * 0.2, -0.35);
      shape.lineTo(0, -0.1);
      shape.closePath();
      const shapeGeo = new THREE.ShapeGeometry(shape);
      const membrane = new THREE.Mesh(shapeGeo, energyMat);
      membrane.position.set(0, 0.2, -0.18);
      membrane.name = 'energyWing';
      wingGroup.add(membrane);
    }

    wingGroup.position.set(0, 0.9, -0.15);
    group.add(wingGroup);
  }
}

// ─── Aura Particle System ────────────────────────────────────────
function buildAuraParticles(group, palette, rng) {
  const particleCount = 200;
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  const sizes = new Float32Array(particleCount);

  const glowColor = new THREE.Color(palette.glow);
  const accentColor = new THREE.Color(palette.accent);

  for (let i = 0; i < particleCount; i++) {
    const angle = rng() * Math.PI * 2;
    const radius = 0.5 + rng() * 1.2;
    const height = rng() * 2.5 - 0.5;

    positions[i * 3] = Math.cos(angle) * radius;
    positions[i * 3 + 1] = height;
    positions[i * 3 + 2] = Math.sin(angle) * radius;

    const mixFactor = rng();
    const c = new THREE.Color().lerpColors(glowColor, accentColor, mixFactor);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;

    sizes[i] = 2 + rng() * 4;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const material = new THREE.PointsMaterial({
    size: 0.04,
    vertexColors: true,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });

  const particles = new THREE.Points(geometry, material);
  particles.name = 'auraParticles';
  group.add(particles);
}

// ─── Scene Setup ─────────────────────────────────────────────────
function createScene(canvas, width, height) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;

  const scene = new THREE.Scene();

  // Camera
  const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 100);
  camera.position.set(0, 0.8, 3.5);
  camera.lookAt(0, 0.6, 0);

  // Lighting
  const ambientLight = new THREE.AmbientLight(0x334466, 0.6);
  scene.add(ambientLight);

  const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
  mainLight.position.set(2, 4, 3);
  mainLight.castShadow = true;
  scene.add(mainLight);

  const fillLight = new THREE.DirectionalLight(0x6688bb, 0.4);
  fillLight.position.set(-2, 1, -1);
  scene.add(fillLight);

  const rimLight = new THREE.DirectionalLight(0x99aacc, 0.6);
  rimLight.position.set(0, 2, -3);
  scene.add(rimLight);

  // Ground disc
  const groundGeo = new THREE.CircleGeometry(1.2, 32);
  const groundMat = new THREE.MeshStandardMaterial({
    color: 0x1a2a3a,
    roughness: 0.8,
    metalness: 0.2,
  });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.25;
  ground.receiveShadow = true;
  scene.add(ground);

  // Ground ring glow
  const ringGeo = new THREE.RingGeometry(1.0, 1.15, 64);
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0x334466,
    transparent: true,
    opacity: 0.3,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = -0.24;
  scene.add(ring);

  return { renderer, scene, camera };
}

// ─── Animation Loop ──────────────────────────────────────────────
function createAnimationLoop(scene, camera, renderer, controls) {
  let animFrame;
  const clock = new THREE.Clock();

  function animate() {
    animFrame = requestAnimationFrame(animate);
    const elapsed = clock.getElapsedTime();

    // Rotate avatar slowly
    const avatar = scene.getObjectByName('avatar');
    if (avatar) {
      // Gentle idle bob
      avatar.position.y = -0.7 + Math.sin(elapsed * 1.5) * 0.03;
    }

    // Animate particles
    const particles = scene.getObjectByName('auraParticles');
    if (particles) {
      particles.rotation.y = elapsed * 0.2;
      const pos = particles.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        pos.array[i * 3 + 1] += Math.sin(elapsed * 2 + i) * 0.001;
      }
      pos.needsUpdate = true;
    }

    // Halo rotation
    const halo = scene.getObjectByName('halo');
    if (halo) {
      halo.rotation.z = elapsed * 0.5;
    }

    // Gem/orb pulse
    const gem = scene.getObjectByName('gem');
    if (gem) {
      gem.material.emissiveIntensity = 0.8 + Math.sin(elapsed * 3) * 0.4;
    }
    const orb = scene.getObjectByName('staffOrb');
    if (orb) {
      orb.material.emissiveIntensity = 1.0 + Math.sin(elapsed * 2) * 0.5;
    }

    // Energy wing pulse
    scene.traverse((child) => {
      if (child.name === 'energyWing') {
        child.material.opacity = 0.2 + Math.sin(elapsed * 2) * 0.1;
      }
    });

    if (controls) controls.update();
    renderer.render(scene, camera);
  }

  animate();

  return () => {
    cancelAnimationFrame(animFrame);
    clock.stop();
  };
}

// ─── Public API ──────────────────────────────────────────────────

/**
 * Generate avatar from text description - returns data URL
 */
export async function generateAvatar(description) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;

    const { mood, features } = analyzeDescription(description);
    const palette = PALETTES[mood];
    const seed = hashString(description);
    const rng = makeRng(seed);

    const { renderer, scene, camera } = createScene(canvas, 512, 512);

    // Background gradient via scene background
    scene.background = new THREE.Color(palette.emissive);

    buildCharacter(scene, description, palette, features, rng);

    // Add colored point light matching palette
    const accentLight = new THREE.PointLight(palette.glow, 0.8, 10);
    accentLight.position.set(0, 2, 2);
    scene.add(accentLight);

    // Render a few frames for particle settling, then capture
    const clock = new THREE.Clock();
    let frames = 0;
    function renderFrames() {
      renderer.render(scene, camera);
      frames++;
      if (frames < 3) {
        requestAnimationFrame(renderFrames);
      } else {
        const dataUrl = canvas.toDataURL('image/png');
        renderer.dispose();
        resolve(dataUrl);
      }
    }
    renderFrames();
  });
}

/**
 * Create a live interactive 3D preview in a container element
 * Returns { canvas, dispose, updateDescription }
 */
export function createLivePreview(container, description = '') {
  const width = container.clientWidth || 400;
  const height = container.clientHeight || 400;

  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.borderRadius = '8px';
  canvas.style.cursor = 'grab';
  container.appendChild(canvas);

  const { renderer, scene, camera } = createScene(canvas, width, height);

  // OrbitControls
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.minDistance = 2;
  controls.maxDistance = 6;
  controls.target.set(0, 0.6, 0);
  controls.autoRotate = true;
  controls.autoRotateSpeed = 1.5;

  let stopAnimation = null;
  let currentAvatar = null;

  function build(desc) {
    // Remove old avatar
    if (currentAvatar) {
      scene.remove(currentAvatar);
      currentAvatar.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material.dispose();
        }
      });
    }

    // Remove old particles and aura-related
    const toRemove = [];
    scene.traverse((child) => {
      if (child.name === 'auraParticles') toRemove.push(child);
    });
    toRemove.forEach(c => scene.remove(c));

    const { mood, features } = analyzeDescription(desc);
    const palette = PALETTES[mood];
    const seed = hashString(desc);
    const rng = makeRng(seed);

    scene.background = new THREE.Color(palette.emissive);

    // Remove and re-add accent light
    const oldAccent = scene.getObjectByName('accentLight');
    if (oldAccent) scene.remove(oldAccent);
    const accentLight = new THREE.PointLight(palette.glow, 0.8, 10);
    accentLight.position.set(0, 2, 2);
    accentLight.name = 'accentLight';
    scene.add(accentLight);

    currentAvatar = buildCharacter(scene, desc, palette, features, rng);
  }

  if (description) {
    build(description);
  }

  stopAnimation = createAnimationLoop(scene, camera, renderer, controls);

  // Handle resize
  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const w = entry.contentRect.width;
      const h = entry.contentRect.height;
      if (w > 0 && h > 0) {
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }
    }
  });
  resizeObserver.observe(container);

  return {
    canvas,
    dispose: () => {
      if (stopAnimation) stopAnimation();
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      if (canvas.parentElement) canvas.parentElement.removeChild(canvas);
    },
    updateDescription: (newDesc) => {
      build(newDesc);
    },
    captureImage: () => {
      renderer.render(scene, camera);
      return canvas.toDataURL('image/png');
    },
  };
}

/**
 * Generate avatar from a preset name
 */
export async function generateAvatarFromPreset(presetName) {
  return generateAvatar(presetName);
}

/**
 * List available presets
 */
/**
 * Generate avatar using DALL-E 3 AI image generation
 * Falls back to procedural 3D if the server endpoint isn't configured
 */
export async function generateAIAvatar(description) {
  try {
    const res = await fetch('/api/avatar/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (data.fallback) {
        // Server says API not configured — use procedural fallback
        console.log('AI avatar not configured, using 3D procedural fallback');
        return { imageUrl: await generateAvatar(description), source: 'procedural' };
      }
      throw new Error(data.error || `Server error ${res.status}`);
    }

    const data = await res.json();
    return { imageUrl: data.imageUrl, source: 'dalle', revisedPrompt: data.revisedPrompt };
  } catch (err) {
    console.warn('AI avatar generation failed, falling back to procedural:', err.message);
    return { imageUrl: await generateAvatar(description), source: 'procedural' };
  }
}

export function getAvailablePresets() {
  return [
    'Guardian Angel', 'Shadow Warrior', 'Nature Spirit',
    'Tech Oracle', 'Cosmic Wanderer', 'Demon Lord',
    'Ice Knight', 'Fire Mage',
  ];
}
